import { useState, useCallback, useEffect } from 'react'
import { message } from 'antd'
import { readPatients } from '../../../../Admin/patients/utils/utils'
import { readClinics } from '../../../../Admin/clinics/utils/utils'
import { readMedicalProfessionalUsers } from '../../../../Admin/medical-professionals/utils/utils'
import { handleError } from '../../../../lib/utils'
import { debounce } from 'lodash'

/**
 * Custom hook for searching entities (patients, practitioners, clinics)
 */
export const useEntitySearch = (type) => {
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchData, setSearchData] = useState([])
  const [searchType, setSearchType] = useState(type)
  const [searchValue, setSearchValue] = useState('')

  // Debounced search to avoid too many API calls
  const debouncedSearch = useCallback(
    debounce((query, type) => {
      if (query) {
        setSearchValue(query)
        setSearchType(type)
      }
    }, 2000),
    []
  )

  // Perform search when search value changes
  useEffect(() => {
    if (searchValue) {
      performSearch()
    } else if (searchType === 'Clinic' || searchType === 'Practitioner') {
      performSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  // Perform the actual search based on type and value
  const performSearch = async () => {
    try {
      let tmpSearchData = []

      switch (searchType) {
        case 'Patient':
        case 'Practitioner':
          // Split search value into terms for better name matching
          const searchTerms = searchValue.trim().toLowerCase().split(/\s+/)

          // Create regex patterns for partial matching
          const regexPatterns = searchTerms.map((term) => new RegExp(term, 'i'))

          const searchQuery = {
            $or: [
              // Match against concatenated firstName and lastName
              {
                $expr: {
                  $regexMatch: {
                    input: { $concat: [{ $toLower: '$firstName' }, ' ', { $toLower: '$lastName' }] },
                    regex: searchTerms.map((term) => `.*${term}.*`).join(''),
                    options: 'i'
                  }
                }
              },
              // Match individually against firstName and lastName
              {
                $or: [
                  {
                    $expr: {
                      $regexMatch: {
                        input: { $toLower: '$firstName' },
                        regex: regexPatterns.join('|'),
                        options: 'i'
                      }
                    }
                  },
                  {
                    $expr: {
                      $regexMatch: {
                        input: { $toLower: '$lastName' },
                        regex: regexPatterns.join('|'),
                        options: 'i'
                      }
                    }
                  }
                ]
              }
            ]
          }

          tmpSearchData = await (searchType === 'Patient'
            ? readPatients(searchQuery)
            : readMedicalProfessionalUsers({ ...searchQuery, 'extraData.profession': 'doctor' }))
          break

        case 'Clinic':
          tmpSearchData = await readClinics({
            name: { $regex: searchValue, $options: 'i' }
          })
          break
        default:
          break
      }

      setSearchData(tmpSearchData)
      setSearchLoading(false)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  // Trigger search when search value changes
  const handleSearch = (value, type) => {
    setSearchLoading(true)
    debouncedSearch(value, type)
  }

  // Reset search when done
  const resetSearch = () => {
    setSearchData([])
    setSearchLoading(false)
    setSearchValue('')
  }

  return {
    searchLoading,
    searchData,
    searchType,
    searchValue,
    handleSearch,
    performSearch,
    resetSearch
  }
}
